import React, { useState, useEffect } from "react";
import queryString from "query-string";
import io from "socket.io-client";

import InfoBar from "../Chat/InfoBar/InfoBar";
import ChatInput from "../Chat/ChatInput/ChatInput";
import MessageList from "../Chat/MessageList/MessageList";
import Game from "../Game/Game";
import NavBar from "../NavBar/NavBar";
import BurgerMenu from "../BurgerMenu/BurgerMenu";
import RoomFull from "../RoomFull/RoomFull";
import Loading from "../Loading/Loading";
import NetworkErrorMsg from "../NetworkErrorMsg/NetworkErrorMsg";

import "./Main.css";

let socket;

const Main = ({ location }) => {
  const [name, setName] = useState("");
  const [room, setRoom] = useState("");
  const [users, setUsers] = useState("");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatboxBool, hideChat] = useState(true);
  const [burgerMenuBool, hideBurgerMenu] = useState(false);
  const [loading, setLoadingStatus] = useState(true); // Change to false to allow development on mobile when hosting server locally
  const [showContentBool, showContent] = useState(false); // Change to true to allow development on mobile when hosting server locally
  const [gameData, setGameData] = useState({});
  const [boardSelectionNum, setSelectedBoard] = useState(0);
  const [roundParams, setRoundParams] = useState({
    playerTurn: 0,
    chameleonID: null,
    secretWordID: null,
  });
  const [roundStarted, setRoundStartedState] = useState(false);
  const [currentVotingData, setNewVotingRecord] = useState({});
  const [voteCountData, recountData] = useState({});
  const [chameleonGuess, setChameleonGuess] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [wordGuessedBool, setWordGuessedBool] = useState(null);
  const [guessedWord, setGuessedWord] = useState(null);
  const [currentScores, updateCurrentScores] = useState({});
  const [roundScore, setRoundScore] = useState({});
  const [showNetworkError, setNetworkError] = useState(false);

  const [currentPromptData, updatePlayerPromptData] = useState({});

  // Points at the back end:
  const ENDPOINT = "https://chamelejon-server.onrender.com/";
  //const ENDPOINT = "localhost:5000";

  useEffect(() => {
    const { name, room } = queryString.parse(location.search);

    socket = io(ENDPOINT, { pingTimeout: 30000 });

    setName(name);
    setRoom(room);

    socket.emit("join", { name, room }, (error) => {
      if (error) {
        // Redirects when names or rooms are >10 characters long, only possible with url manipulation
        if (error.status === 401) {
          setLoadingStatus(true);
          return (window.location.href = `/`);
        }

        // Handles users with the same name
        if (name && room) {
          const findNumbers = (nameArray, nameNum) => {
            let lastChar = nameArray.slice(-1).pop();

            if (isNaN(lastChar) === false) {
              nameNum.push(lastChar);
              let newArr = nameArray.splice(0, nameArray.length - 1);
              if (isNaN(newArr.slice(-1).pop()) === false) {
                return nameNum + findNumbers(newArr, nameNum);
              } else
                return (window.location.href = `/game?name=${newArr.join("")}${(
                  Number(nameNum.reverse().join("")) + 1
                ).toString()}&room=${room}`);
            }
          };

          let nameLastChar = parseInt(name.split("").slice(-1).pop());
          if (isNaN(nameLastChar) === false) {
            let nameNum = [];
            const nameArr = name.split("");
            findNumbers(nameArr, nameNum);
          } else
            return (window.location.href = `/game?name=${name}1&room=${room}`);
        }

        //Handles no name specified
        if (!name && room) {
          return (window.location.href = `/game?name=guest&room=${room}`);
        }

        // Handles no room specified
        if (!room) {
          return (window.location.href = `/`);
        }
      }
    });

    return () => {
      socket.emit("disconnect");
      socket.disconnect();
    };
  }, [ENDPOINT, location.search]);

  useEffect(() => {
    document.body.style.position = "";
    socket.on("message", (message) => {
      setMessages((msgs) => [...msgs, message]);
    });
    socket.on("roomData", ({ users }) => {
      setUsers(users);
    });
    socket.on("roomFull", function () {
      setLoadingStatus(false);
      socket.disconnect();
    });
    socket.on("allowEntry", function (entryStatus) {
      setLoadingStatus(false);
      showContent(entryStatus);
    });
    socket.on("gameData", (gameDataObj) => {
      setGameData(gameDataObj);
    });
    socket.on("boardSelectionData", (boardNum) => {
      setSelectedBoard(boardNum);
    });
    socket.on("roundParamsData", (roundParamsObj) => {
      setRoundParams(roundParamsObj);
    });
    socket.on("roundStartedData", (roundStartedBool) => {
      setRoundStartedState(roundStartedBool);
      setShowResults(false);
    });
    socket.on("setNewVoteData", (voteDataObj, currentvoteDataObj) => {
      setNewVotingRecord(voteDataObj);
      calcPlayerVotes(currentvoteDataObj);
    });
    socket.on("resetAllData", () => {
      setSelectedBoard(0);
      setRoundParams({ playerTurn: 0, chameleonID: null, secretWordID: null });
      setRoundStartedState(false);
      setNewVotingRecord({});
      recountData({});
      setChameleonGuess(false);
      setShowResults(false);
      setWordGuessedBool(null);
      setGuessedWord(null);
      setRoundScore({});
      updatePlayerPromptData({});
    });
    socket.on("resetAllRoundData", () => {
      setNewVotingRecord({});
      recountData({});
      setChameleonGuess(false);
      setShowResults(false);
      setWordGuessedBool(null);
      setGuessedWord(null);
      setRoundScore({});
      updatePlayerPromptData({});
    });
    socket.on("returnGuessToRoom", (guessBool, selectedWord) => {
      setGuessedWord(selectedWord);
      setWordGuessedBool(guessBool);
      setShowResults(true);
    });
    socket.on("returnScoresToRoom", (roundScores, overallScores) => {
      updateCurrentScores(overallScores);
      setRoundScore(roundScores);
    });
    socket.on("returnSyncedScores", (scoreFromHost) => {
      if (Object.keys(scoreFromHost).length !== 0) {
        updateCurrentScores(scoreFromHost);
      }
    });
    socket.on("setPlayerPromptData", (promptDataObj) => {
      updatePlayerPromptData(promptDataObj);
    });
  }, []);

  const sendMessage = (event) => {
    event.preventDefault();
    if (message) {
      socket.emit("sendMessage", message, () => setMessage(""));
    }
  };

  const dropChat = (chatboxBool) => {
    hideChat(!chatboxBool);
  };

  const toggleBurgerMenu = (burgerMenuBool) => {
    hideBurgerMenu(!burgerMenuBool);
    if (burgerMenuBool === false) {
      document.body.style.position = "fixed";
      return;
    } else document.body.style.position = "";
  };

  const readyPlayer = (event, name) => {
    event.preventDefault();
    socket.emit("readyPlayer", name, users);
  };

  const startGame = () => {
    socket.emit("startGame", { room });
    resyncScoreData();
  };

  // Game related events
  const updateBoard = (boardData) => {
    socket.emit("updateBoard", boardData);
  };

  const updateRoundParams = (roundParamsData) => {
    socket.emit("updateRoundParams", roundParamsData);
  };

  const updateRoundStarted = (roundStartedData) => {
    socket.emit("updateRoundStarted", !roundStartedData);
  };

  const updateVoteData = (playerVoteData, sourceOfVote) => {
    socket.emit("votePlayer", playerVoteData, sourceOfVote, currentVotingData);

    if (Object.keys(currentVotingData).length === users.length - 1) {
      // Creating a localised copy of voting data (seperate to whats sent on the back end)
      if (currentVotingData !== undefined) {
        var localisedVoteInfo = currentVotingData;
        localisedVoteInfo[sourceOfVote] = playerVoteData;

        // Combine the tally of votes into a useable object for vote calculations
        let LocalVoteArray = [];

        for (const val in localisedVoteInfo) {
          LocalVoteArray.push(localisedVoteInfo[val]);
        }

        const summedLocalVotes = LocalVoteArray.reduce(function (tally, item) {
          if (!tally[item]) {
            tally[item] = 1;
          } else {
            tally[item] = tally[item] + 1;
          }
          return tally;
        }, {});

        // Calculate the highest voted player
        const highestVoteCounted = Math.max.apply(
            null,
            Object.values(summedLocalVotes)
          ),
          localisedVotedPlayerName = Object.keys(summedLocalVotes).filter(
            function (x) {
              return summedLocalVotes[x] === highestVoteCounted;
            }
          );

        // If chameleon has not been ousted then this is the time to calculate
        console.log(localisedVotedPlayerName);
        if (
          localisedVotedPlayerName[0] !== users[roundParams?.chameleonID]?.name
        ) {
          calcScores(localisedVoteInfo, wordGuessedBool);
        }
      }
    }
  };

  const resetRoundData = () => {
    socket.emit("resetEveryonesRound");
  };

  const sendGuessedWord = (result, selectedWord) => {
    socket.emit("sendGuessedWord", result, selectedWord);
  };

  const updateScoreData = (roundScores) => {
    socket.emit("updateScore", roundScores, currentScores);
  };

  const resyncScoreData = () => {
    socket.emit("resyncScore", currentScores);
  };

  const updatePromptData = (playerPromptData, sourceOfData) => {
    socket.emit(
      "updatePrompt",
      playerPromptData,
      sourceOfData,
      currentPromptData
    );
  };

  const calcPlayerVotes = (currentvoteDataObj) => {
    let voteArray = [];

    for (const val in currentvoteDataObj) {
      voteArray.push(currentvoteDataObj[val]);
    }

    const voteCounter = voteArray.reduce(function (tally, item) {
      if (!tally[item]) {
        tally[item] = 1;
      } else {
        tally[item] = tally[item] + 1;
      }
      return tally;
    }, {});

    recountData(voteCounter);
  };

  const calcScores = (currentVotingData, wordGuessedBool) => {
    const highestVoteCount = Math.max.apply(null, Object.values(voteCountData)),
      votedPlayerName = Object.keys(voteCountData).filter(function (x) {
        return voteCountData[x] === highestVoteCount;
      });

    let playerWhoStarted = users[roundParams?.playerTurn]?.name;
    let chameleon = users[roundParams?.chameleonID]?.name;

    let roundScoreArr = [];
    for (const player in currentVotingData) {
      roundScoreArr.push({ [player]: 0 });
    }

    // merges array into single object
    let combinedObj = Object.assign(...roundScoreArr);

    // No consensus/ Chameleon undetected
    if (votedPlayerName.length > 1 || votedPlayerName[0] !== chameleon) {
      //The Chameleon escapes undetected: 3 points for the chameleon
      combinedObj[chameleon] = 3;

      //The Chameleon gets 2 bonus points for winning if they started the round.
      if (playerWhoStarted === chameleon) {
        combinedObj[chameleon] = 5;
      }

      //  1 point for those who correctly voted for the chameleon, despite being in the minority.
      users.forEach((user) => {
        if (currentVotingData[user.name] === chameleon) {
          combinedObj[user.name] = 1;
        }
      });
    }

    // Chameleon caught but guesses word
    if (wordGuessedBool === true) {
      // The Chameleon is caught but guesses the secret word: 1 point for the Chameleon.
      combinedObj[chameleon] = 1;
    }

    // Chameleon caught + doesn't know word
    if (wordGuessedBool === false) {
      // The Chameleon is caught and doesn't know the secret word: 2 points for everyone but the Chameleon.
      users.forEach((user) => {
        if (user.name !== chameleon) {
          combinedObj[user.name] = 2;
        }
      });
    }
    updateScoreData(combinedObj);
  };

  // Detecting clientside timeouts
  function getTime() {
    return new Date().getTime();
  }

  var lastInterval = getTime();

  function intervalHeartbeat() {
    var now = getTime();
    var diff = now - lastInterval;
    var offBy = diff;
    lastInterval = now;

    // Will display the network error message when the internal heartbeat fails (aka JS has stopped running)
    if (offBy > 10000) {
      setNetworkError(true);
    }
  }

  setInterval(intervalHeartbeat, 1000);

  if (loading !== true) {
    if (showContentBool) {
      return (
        <div className="mainOuterContainer">
          {burgerMenuBool === true ? <div className="coverShim"></div> : null}
          <NavBar
            burgerMenuBool={burgerMenuBool}
            toggleBurgerMenu={toggleBurgerMenu}
          />
          <div className="mainInnerContainer">
            <BurgerMenu
              burgerMenuBool={burgerMenuBool}
              toggleBurgerMenu={toggleBurgerMenu}
              users={users}
            />
            <Game
              users={users}
              name={name}
              readyPlayer={readyPlayer}
              chatboxBool={chatboxBool}
              startGame={startGame}
              gameData={gameData}
              room={room}
              updateBoard={updateBoard}
              boardSelectionNum={boardSelectionNum}
              updateRoundParams={updateRoundParams}
              roundParams={roundParams}
              roundStarted={roundStarted}
              updateRoundStarted={updateRoundStarted}
              updateVoteData={updateVoteData}
              voteCountData={voteCountData}
              currentVotingData={currentVotingData}
              resetRoundData={resetRoundData}
              chameleonGuess={chameleonGuess}
              setChameleonGuess={setChameleonGuess}
              showResults={showResults}
              setShowResults={setShowResults}
              sendGuessedWord={sendGuessedWord}
              guessedWord={guessedWord}
              currentScores={currentScores}
              roundScore={roundScore}
              calcScores={calcScores}
              updatePromptData={updatePromptData}
              currentPromptData={currentPromptData}
            />
            <div className={chatboxBool ? "hidden" : "chatContainer"}>
              <InfoBar
                room={room}
                users={users}
                chatboxBool={chatboxBool}
                dropChat={dropChat}
              />
              <MessageList
                messages={messages}
                name={name}
                chatboxBool={chatboxBool}
              />
              <ChatInput
                message={message}
                setMessage={setMessage}
                sendMessage={sendMessage}
                chatboxBool={chatboxBool}
              />
              {showNetworkError === true ? (
                <div>
                  <div className="shimContainer"></div>
                  <NetworkErrorMsg />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
    } else return <RoomFull />;
  } else return <Loading />;
};

export default Main;
